import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Camera, X, Calendar, AlertTriangle } from 'lucide-react';
import { SkeletonList } from '../components/ui/Skeleton';
import { PageShell } from '../components/ui/PageShell';
import { SectionGroup } from '../components/ui/SectionGroup';
import { getStatusLabel as uiGetStatusLabel } from '../components/ui/StatusBadge';
import { Timestamp } from 'firebase/firestore';
import { Project } from '../types';
import { createProject, getProject, updateProject, deleteProject } from '../services/projectService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { associateTemporaryPhotos, getTemporaryPhotos } from '../services/photoService';
import TemporaryPhotoComponent from '../components/TemporaryPhotoComponent';
import CalendarSyncStatus from '../components/CalendarSyncStatus';
import { isAuthenticated, getAvailableTimeSlots, checkTimeSlotOverlap } from '../services/googleCalendarService';
import { useFormNavigation } from '../hooks/useFormNavigation';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { useFullScreen } from '../components/Layout';
import AiAssistButton from '../components/AiAssistButton';
import { fixAddress, polishText } from '../services/aiAssistService';
import { TEAM_MEMBERS } from '../constants/people';

// Custom TimeSelect component for half-hour increments
const TimeSelect: React.FC<{
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
  date?: Date | null;
  availableSlots?: { time: string, available: boolean }[];
  isLoading?: boolean;
}> = ({ id, name, value, onChange, className, date, availableSlots, isLoading }) => {
  // Helper function to check if a date/time is in the past
  const isPassedDateTime = (dateParam: Date | null | undefined, time: string): boolean => {
    if (!dateParam) return false;
    const [hours, minutes] = time.split(':').map(Number);
    const dateTime = new Date(dateParam);
    dateTime.setHours(hours, minutes, 0, 0);
    return dateTime < new Date();
  };

  // Check if the selected date is in the past
  const isPassedDate = date !== undefined && date !== null && date < new Date(new Date().setHours(0, 0, 0, 0));

  // Generate time options from 06:00 to 20:00 in half-hour increments
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 6; hour <= 20; hour++) {
      const formattedHour = hour.toString().padStart(2, '0');
      options.push(`${formattedHour}:00`);
      options.push(`${formattedHour}:30`);
    }
    return options;
  };

  // A calendar-imported time like 11:40 is not a half-hour step; list it so
  // the select can show it instead of falling back to "Välj tid".
  const base = generateTimeOptions();
  const timeOptions = value && !base.includes(value) ? [...base, value].sort() : base;

  return (
    <div>
      <select
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        className={`${className || "w-full px-3.5 py-2.5 bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 transition-colors"} 
          ${isPassedDate ? 'bg-[var(--bg-primary)] border-amber-200' : ''}`}
        disabled={isLoading}
      >
        <option value="">Välj tid</option>
        {timeOptions.map((time) => {
          // If we have availability data, check if this time is available
          const slot = availableSlots?.find(slot => slot.time === time);
          const isAvailable = !availableSlots || !slot || slot.available;
          const isPassed = isPassedDateTime(date, time);

          return (
            <option
              key={time}
              value={time}
              // Tokens, not hex. The old values (#9CA3AF on #F3F4F6 = 2.2:1,
              // #999 on white = 2.8:1) failed WCAG AA in both themes and were
              // invisible in dark mode, since the light gray fill never swapped.
              style={{
                color: isPassed || !isAvailable ? 'var(--text-tertiary)' : 'inherit',
                fontStyle: isPassed || !isAvailable ? 'italic' : 'normal',
                backgroundColor: isPassed ? 'var(--bg-surface-alt)' : 'inherit'
              }}
            >
              {time}
              {isPassed ? ' (passerad)' : (!isAvailable ? ' (upptagen)' : '')}
            </option>
          );
        })}
      </select>
      {isLoading && (
        <div className="mt-1 text-xs text-[var(--text-secondary)] flex items-center">
          <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-on-surface-tertiary mr-1"></div>
          <span className="text-xs">Kontrollerar tillgänglighet...</span>
        </div>
      )}
      {isPassedDate && (
        <div className="mt-1 text-xs text-amber-700 dark:text-amber-400 flex items-center">
          <AlertTriangle size={12} className="mr-1 flex-shrink-0" aria-hidden="true" />
          <span>Detta datum har redan passerat</span>
        </div>
      )}
    </div>
  );
};

// FormRow must be defined outside the component to avoid remounting inputs on every render
const FormRow: React.FC<{
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  last?: boolean;
}> = ({ label, required, error, children, last }) => (
  <div className={`flex items-center gap-3 px-4 py-3 min-h-[44px] ${last ? '' : 'inset-divider'}`}>
    <label className="text-[15px] text-[var(--text-primary)] w-24 sm:w-28 flex-shrink-0">
      {label}{required && <span className="text-red-700 dark:text-red-300 ml-0.5">*</span>}
    </label>
    <div className="flex-1 min-w-0">
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  </div>
);

type ProjectFormData = {
  address: {
    street: string;
    city: string;
    postalCode: string;
    accessCode: string;
    accessInfo: string;
    coordinates: { lat: number, lng: number } | null;
  };
  contact: {
    name: string;
    phone: string;
    email?: string;
  };
  dates: {
    evaluation: Timestamp | null;
    evaluationStartTime: string;
    evaluationEndTime: string;
    scheduledPickup: Timestamp | null;
    scheduledPickupTime: string;
    actualPickup: Timestamp | null;
  };
  status: Project['status'];
  generalNotes: string;
  evaluator: string;
  pickupTeam: string[];
};

const ProjectFormPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { setIsFullScreen } = useFullScreen();
  const isEditMode = !!projectId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formChanged, setFormChanged] = useState(false);
  const [initialFormData, setInitialFormData] = useState<ProjectFormData | null>(null);
  const [project, setProject] = useState<Project | null>(null);

  const [formData, setFormData] = useState<ProjectFormData>({
    address: {
      street: '',
      city: '',
      postalCode: '',
      accessCode: '',
      accessInfo: '',
      coordinates: null
    },
    contact: {
      name: '',
      phone: '',
      email: ''
    },
    dates: {
      evaluation: null,
      evaluationStartTime: '',
      evaluationEndTime: '',
      scheduledPickup: null,
      scheduledPickupTime: '',
      actualPickup: null
    },
    status: 'bostad',
    generalNotes: '',
    evaluator: '',
    pickupTeam: []
  });

  const [showTempPhotos, setShowTempPhotos] = useState(false);
  const [tempPhotoCount, setTempPhotoCount] = useState(0);
  const [newTeamMember, setNewTeamMember] = useState('');
  const [customTeamMember, setCustomTeamMember] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // New state for calendar availability
  const [isCalendarAuthenticated, setIsCalendarAuthenticated] = useState(false);
  const [evaluationDateSlots, setEvaluationDateSlots] = useState<{ time: string, available: boolean }[]>([]);
  const [pickupDateSlots, setPickupDateSlots] = useState<{ time: string, available: boolean }[]>([]);
  const [loadingEvaluationSlots, setLoadingEvaluationSlots] = useState(false);
  const [loadingPickupSlots, setLoadingPickupSlots] = useState(false);
  const [calendarOverlapWarning, setCalendarOverlapWarning] = useState<string | null>(null);
  // Parse the overlap-warning JSON once, defensively. Parsing inline in JSX
  // (three times) would crash the whole form render if the string were ever
  // malformed; here a parse failure just hides the warning instead.
  const parsedOverlapWarning = useMemo<{ message?: string; events?: any[]; type?: string } | null>(() => {
    if (!calendarOverlapWarning) return null;
    try {
      return JSON.parse(calendarOverlapWarning);
    } catch (err) {
      console.error('Failed to parse calendarOverlapWarning:', err);
      return null;
    }
  }, [calendarOverlapWarning]);

  const pickupSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) {
        setLoading(false);
        setInitialFormData(formData);
        return;
      }

      try {
        setLoading(true);
        const fetchedProject = await getProject(projectId);

        if (!fetchedProject) {
          setError('Projektet kunde inte hittas.');
          setLoading(false);
          return;
        }

        const projectFormData = {
          address: {
            street: fetchedProject.address.street,
            city: fetchedProject.address.city,
            postalCode: fetchedProject.address.postalCode,
            accessCode: fetchedProject.address.accessCode || '',
            accessInfo: fetchedProject.address.accessInfo || '',
            coordinates: fetchedProject.address.coordinates
          },
          contact: {
            name: fetchedProject.contact.name,
            phone: fetchedProject.contact.phone,
            email: fetchedProject.contact.email
          },
          dates: {
            evaluation: fetchedProject.dates.evaluation,
            evaluationStartTime: fetchedProject.dates.evaluationStartTime || '',
            evaluationEndTime: fetchedProject.dates.evaluationEndTime || '',
            scheduledPickup: fetchedProject.dates.scheduledPickup,
            scheduledPickupTime: fetchedProject.dates.scheduledPickupTime || '',
            actualPickup: fetchedProject.dates.actualPickup
          },
          status: fetchedProject.status,
          generalNotes: fetchedProject.generalNotes,
          evaluator: fetchedProject.evaluator,
          pickupTeam: fetchedProject.pickupTeam || []
        };

        setFormData(projectFormData);
        setInitialFormData(projectFormData);
        setProject(fetchedProject);
        setError(null);
      } catch (err) {
        console.error('Error fetching project:', err);
        setError('Kunde inte hämta projektdata. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  // Track form changes
  useEffect(() => {
    if (loading || !initialFormData) return;

    // Compare current form data with initial data
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    setFormChanged(hasChanges);
  }, [formData, initialFormData, loading]);

  // Check for temporary photos
  useEffect(() => {
    const tempPhotos = getTemporaryPhotos();
    setTempPhotoCount(tempPhotos.length);
  }, [showTempPhotos]);

  // Check if user is authenticated with Google Calendar
  useEffect(() => {
    const checkAuth = async () => {
      const authenticated = await isAuthenticated();
      setIsCalendarAuthenticated(authenticated);
    };

    checkAuth();
  }, []);

  // Fetch available slots when evaluation date changes
  useEffect(() => {
    if (!isCalendarAuthenticated || !formData.dates.evaluation) return;

    const fetchEvaluationSlots = async () => {
      setLoadingEvaluationSlots(true);
      try {
        // We can safely use toDate() here because we've checked that evaluation is not null above
        const date = formData.dates.evaluation!.toDate();

        // Get the evaluation event ID if in edit mode
        let evaluationEventId = null;
        if (project && project.id) {
          evaluationEventId = localStorage.getItem(`evaluation_event_${project.id}`);
        }

        const slots = await getAvailableTimeSlots(date, evaluationEventId || undefined);
        setEvaluationDateSlots(slots);
      } catch (error) {
        console.error('Error fetching evaluation slots:', error);
      } finally {
        setLoadingEvaluationSlots(false);
      }
    };

    fetchEvaluationSlots();
  }, [isCalendarAuthenticated, formData.dates.evaluation, project]);

  // Fetch available slots when pickup date changes
  useEffect(() => {
    if (!isCalendarAuthenticated || !formData.dates.scheduledPickup) return;

    const fetchPickupSlots = async () => {
      setLoadingPickupSlots(true);
      try {
        if (formData.dates.scheduledPickup) {
          const date = formData.dates.scheduledPickup.toDate();

          // Get the pickup event ID if in edit mode
          let pickupEventId = null;
          if (project && project.id) {
            pickupEventId = localStorage.getItem(`pickup_event_${project.id}`);
          }

          const slots = await getAvailableTimeSlots(date, pickupEventId || undefined);
          setPickupDateSlots(slots);
        }
      } catch (error) {
        console.error('Error fetching pickup slots:', error);
      } finally {
        setLoadingPickupSlots(false);
      }
    };

    fetchPickupSlots();
  }, [isCalendarAuthenticated, formData.dates.scheduledPickup, project]);

  // Check for overlaps when time is selected
  useEffect(() => {
    const checkOverlap = async () => {
      if (!isCalendarAuthenticated) return;

      // Clear previous warnings only when dependencies change
      if (calendarOverlapWarning) {
        setCalendarOverlapWarning(null);
        return; // Exit early to prevent flickering - will run again after state update
      }

      // Get stored event IDs if this is an edit mode
      let evaluationEventId = null;
      let pickupEventId = null;

      if (project && project.id) {
        evaluationEventId = localStorage.getItem(`evaluation_event_${project.id}`);
        pickupEventId = localStorage.getItem(`pickup_event_${project.id}`);
      }

      // Check evaluation time overlap
      if (formData.dates.evaluation && formData.dates.evaluationStartTime) {
        try {
          const date = formData.dates.evaluation.toDate();

          // Calculate end time if not provided
          let endTime = formData.dates.evaluationEndTime;
          if (!endTime && formData.dates.evaluationStartTime) {
            // Default to 1 hour duration
            const startHour = parseInt(formData.dates.evaluationStartTime.split(':')[0]);
            const startMinute = parseInt(formData.dates.evaluationStartTime.split(':')[1]);
            const endHour = (startHour + 1) % 24;
            const endMinute = startMinute;
            endTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
          }

          if (endTime) {
            const { hasOverlap, overlappingEventDetails } = await checkTimeSlotOverlap(
              date,
              formData.dates.evaluationStartTime,
              endTime,
              evaluationEventId || undefined
            );

            if (hasOverlap && overlappingEventDetails.length > 0) {
              // Store the full event details instead of formatting them as a string
              setCalendarOverlapWarning(JSON.stringify({
                type: 'evaluation',
                message: 'Hembesökstiden sammanfaller med följande händelser:',
                events: overlappingEventDetails.map(event => ({
                  ...event,
                  time: `${event.startTime}-${event.endTime} (${formData.dates.evaluationStartTime}-${endTime})`
                }))
              }));
              return; // Exit early to prevent multiple warnings
            }

            // Also check if evaluation time overlaps with pickup time (if pickup is scheduled)
            if (formData.dates.scheduledPickup && formData.dates.scheduledPickupTime && formData.dates.evaluation) {
              const evalDate = new Intl.DateTimeFormat('sv-SE').format(formData.dates.evaluation.toDate());
              const pickupDate = new Intl.DateTimeFormat('sv-SE').format(formData.dates.scheduledPickup.toDate());

              if (evalDate === pickupDate) {
                // Calculate pickup end time (default 2 hours)
                const pickupStartHour = parseInt(formData.dates.scheduledPickupTime.split(':')[0]);
                const pickupStartMinute = parseInt(formData.dates.scheduledPickupTime.split(':')[1]);
                const pickupEndHour = (pickupStartHour + 2) % 24;
                const pickupEndMinute = pickupStartMinute;
                const pickupEndTime = `${pickupEndHour.toString().padStart(2, '0')}:${pickupEndMinute.toString().padStart(2, '0')}`;

                // Check if evaluation time overlaps with pickup time
                const evalStartTime = formData.dates.evaluationStartTime;
                const evalEndTime = endTime;
                const pickupStartTime = formData.dates.scheduledPickupTime;

                // Simple overlap check
                const evalStart = parseInt(evalStartTime.replace(':', ''));
                const evalEnd = parseInt(evalEndTime.replace(':', ''));
                const pickupStart = parseInt(pickupStartTime.replace(':', ''));
                const pickupEnd = parseInt(pickupEndTime.replace(':', ''));

                if ((evalStart <= pickupEnd && evalEnd >= pickupStart)) {
                  setCalendarOverlapWarning(JSON.stringify({
                    type: 'internal',
                    message: 'Hembesökstiden och upphämtningstiden överlappar varandra:',
                    events: [{
                      summary: 'Hembesök och upphämtning',
                      location: formData.address.street + ', ' + formData.address.city,
                      time: `Hembesök: ${evalStartTime}-${evalEndTime}, Upphämtning: ${pickupStartTime}-${pickupEndTime}`
                    }]
                  }));
                  return; // Exit early to prevent multiple warnings
                }
              }
            }
          }
        } catch (error) {
          console.error('Error checking evaluation overlap:', error);
        }
      }

      // Check pickup time overlap
      if (formData.dates.scheduledPickup && formData.dates.scheduledPickupTime) {
        try {
          const date = formData.dates.scheduledPickup.toDate();

          // Calculate end time for pickup (default 2 hours)
          const startHour = parseInt(formData.dates.scheduledPickupTime.split(':')[0]);
          const startMinute = parseInt(formData.dates.scheduledPickupTime.split(':')[1]);
          const endHour = (startHour + 2) % 24;
          const endMinute = startMinute;
          const endTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;

          const { hasOverlap, overlappingEventDetails } = await checkTimeSlotOverlap(
            date,
            formData.dates.scheduledPickupTime,
            endTime,
            pickupEventId || undefined
          );

          if (hasOverlap && overlappingEventDetails.length > 0) {
            // Store the full event details instead of formatting them as a string
            setCalendarOverlapWarning(JSON.stringify({
              type: 'pickup',
              message: 'Upphämtningstiden sammanfaller med följande händelser:',
              events: overlappingEventDetails.map(event => ({
                ...event,
                time: `${event.startTime}-${event.endTime} (${formData.dates.scheduledPickupTime}-${endTime})`
              }))
            }));
          }
        } catch (error) {
          console.error('Error checking pickup overlap:', error);
        }
      }
    };

    checkOverlap();
  }, [
    isCalendarAuthenticated,
    formData.dates.evaluation,
    formData.dates.evaluationStartTime,
    formData.dates.evaluationEndTime,
    formData.dates.scheduledPickup,
    formData.dates.scheduledPickupTime,
    project,
    // Remove calendarOverlapWarning from dependencies to prevent flickering
    // calendarOverlapWarning
  ]);

  // Handle time selection and automatically set end time
  const handleTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Update the selected time
    setFormData(prev => ({
      ...prev,
      dates: {
        ...prev.dates,
        [name.split('.')[1]]: value
      }
    }));

    // If this is the start time for evaluation, automatically set end time to be 1 hour later
    if (name === 'dates.evaluationStartTime' && value) {
      const [hours, minutes] = value.split(':').map(Number);
      const endHour = (hours + 1) % 24;
      const endTime = `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

      setFormData(prev => ({
        ...prev,
        dates: {
          ...prev.dates,
          evaluationEndTime: endTime
        }
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // For time fields, use the special handler
    if (name === 'dates.evaluationStartTime' || name === 'dates.evaluationEndTime' || name === 'dates.scheduledPickupTime') {
      handleTimeChange(e as React.ChangeEvent<HTMLSelectElement>);
      return;
    }

    // Handle nested properties
    if (name.includes('.')) {
      const [parent, child] = name.split('.');

      if (parent === 'address') {
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            [child]: value
          }
        }));
      } else if (parent === 'contact') {
        setFormData(prev => ({
          ...prev,
          contact: {
            ...prev.contact,
            [child]: value
          }
        }));
      } else if (parent === 'dates') {
        setFormData(prev => ({
          ...prev,
          dates: {
            ...prev.dates,
            [child]: value
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const fieldName = name.split('.')[1]; // Extract the field name without the section prefix

    if (value) {
      setFormData(prev => ({
        ...prev,
        dates: {
          ...prev.dates,
          [fieldName]: Timestamp.fromDate(new Date(value))
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        dates: {
          ...prev.dates,
          [fieldName]: null
        }
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const projectId = await handleSaveAndNavigate();

    if (projectId) {
      // Redirect to project detail page after saving
      navigate(`/projects/${projectId}`);
    }
  };

  const handleSaveAndNavigate = async (): Promise<string | false> => {
    if (!isOnline) {
      setError('Du är offline. Anslut till internet för att spara projektet.');
      return false;
    }

    const errors: Record<string, string> = {};
    if (!formData.address.street.trim()) errors['address.street'] = 'Gatuadress krävs';
    if (!formData.address.city.trim()) errors['address.city'] = 'Ort krävs';
    if (!formData.contact.name.trim()) errors['contact.name'] = 'Kontaktperson krävs';
    if (!formData.contact.phone.trim()) errors['contact.phone'] = 'Telefonnummer krävs';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Fyll i alla obligatoriska fält.');
      // Take the user to the problem. This form is ~6 sections long, so a
      // banner alone can sit several screens above the save button the user
      // just tapped — on a phone that reads as "nothing happened". Errors are
      // keyed by the input's `name`, so the first one can be focused directly.
      const firstErrorKey = Object.keys(errors)[0];
      requestAnimationFrame(() => {
        const field = document.querySelector<HTMLElement>(
          `[name="${CSS.escape(firstErrorKey)}"]`
        );
        field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        field?.focus({ preventScroll: true });
      });
      return false;
    }
    setFieldErrors({});

    try {
      setSaving(true);

      const projectData = {
        address: {
          street: formData.address.street.trim(),
          city: formData.address.city.trim(),
          postalCode: formData.address.postalCode.trim(),
          accessCode: formData.address.accessCode.trim(),
          accessInfo: formData.address.accessInfo.trim(),
          coordinates: formData.address.coordinates
        },
        contact: {
          name: formData.contact.name.trim(),
          phone: formData.contact.phone.trim(),
          email: formData.contact.email?.trim()
        },
        dates: {
          evaluation: formData.dates.evaluation,
          evaluationStartTime: formData.dates.evaluationStartTime,
          evaluationEndTime: formData.dates.evaluationEndTime,
          scheduledPickup: formData.dates.scheduledPickup,
          scheduledPickupTime: formData.dates.scheduledPickupTime,
          actualPickup: formData.dates.actualPickup
        },
        status: formData.status,
        generalNotes: formData.generalNotes.trim(),
        evaluator: formData.evaluator.trim(),
        pickupTeam: formData.pickupTeam
      };

      if (project && project.id) {
        await updateProject(project.id, projectData);
        setInitialFormData(formData);
        setFormChanged(false);
        return project.id;
      } else {
        const result = await createProject(projectData);
        setInitialFormData(formData);
        setFormChanged(false);
        return result.id;
      }
    } catch (err) {
      console.error('Error saving project:', err);
      setError('Kunde inte spara projektet. Försök igen senare.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Form navigation safety
  const {
    navigateSafely,
    showDialog,
    handleSave,
    handleDiscard,
    handleContinueEditing,
    isSaving
  } = useFormNavigation({
    onSave: handleSaveAndNavigate,
    hasUnsavedChanges: formChanged
  });

  const handleDelete = () => {
    if (!projectId) return;

    if (!isOnline) {
      setError('Du är offline. Anslut till internet för att ta bort projektet.');
      return;
    }

    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!projectId) return;

    try {
      setSaving(true);
      await deleteProject(projectId);
      setShowDeleteConfirm(false);
      navigate('/');
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Kunde inte ta bort projektet. Försök igen senare.');
    } finally {
      setSaving(false);
    }
  };

  // Add useEffect to handle fullscreen mode
  useEffect(() => {
    // Hide footer when component mounts
    setIsFullScreen(true);

    // Show footer when component unmounts
    return () => setIsFullScreen(false);
  }, [setIsFullScreen]);

  // Update the scroll effect
  useEffect(() => {
    // Only attempt to scroll after loading is complete
    if (!loading) {
      const scrollToSection = sessionStorage.getItem('scrollToSection');
      if (scrollToSection === 'pickup' && pickupSectionRef.current) {
        // Clear the storage item
        sessionStorage.removeItem('scrollToSection');
        
        // Use a small delay to ensure the form is fully rendered
        setTimeout(() => {
          pickupSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [loading]); // Only re-run when loading state changes

  if (loading) {
    return (
      <div className="px-4 py-3 space-y-3">
        <SkeletonList count={5} />
      </div>
    );
  }

  const backLink = project ? `/projects/${project.id}` : '/';

  // Helper function to check if a project has passed dates
  const hasPassedDates = (dates: ProjectFormData['dates']): boolean => {
    if (!dates) return false;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // If a pickup date is set, don't show the warning banner
    if (dates.scheduledPickup) return false;

    // Only show warning if evaluation date has passed and no pickup date is set
    return Boolean(dates.evaluation && dates.evaluation.toDate() < today);
  };

  // Shared input class for inline form rows
  const inputClass = "bg-transparent text-[15px] text-[var(--text-primary)] w-full placeholder-[var(--text-tertiary)] outline-none py-0";
  const inputClassRight = `${inputClass} text-right`;
  const selectClass = `${inputClass} appearance-none cursor-pointer`;
  const selectClassRight = `${selectClass} text-right`;

  return (
    <>
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Ta bort"
        message="Är du säker på att du vill ta bort detta projekt? Alla tillhörande rum, föremål och skaderapporter kommer också att tas bort. Denna åtgärd kan inte ångras."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        loading={saving}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <UnsavedChangesDialog
        isOpen={showDialog}
        onClose={handleContinueEditing}
        onDiscard={handleDiscard}
        onSave={handleSave}
      />

      <PageShell
        title={isEditMode ? 'Redigera' : 'Nytt projekt'}
        onBack={() => navigateSafely(backLink)}
        backLabel="Projekt"
        headerRight={
          <div className="flex items-center gap-4">
            {isEditMode && (
              <button
                type="button"
                onClick={handleDelete}
                className="text-red-600 dark:text-red-400 active:text-red-800 text-[17px] font-normal transition-colors"
              >
                Radera
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className={`text-[17px] font-semibold transition-colors ${
                saving
                  ? 'text-[var(--text-tertiary)]'
                  : 'text-accent-600 dark:text-accent-400 active:opacity-50'
              }`}
            >
              {saving ? 'Sparar…' : 'Spara'}
            </button>
          </div>
        }
      >
        <form onSubmit={(e) => e.preventDefault()} className="space-y-6 pb-8">

          {/* Past Date Warning Banner */}
          {hasPassedDates(formData.dates) && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-400 px-3 py-2.5 rounded-ios-card">
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[13px] font-medium text-amber-800 dark:text-amber-300">Passerade datum</p>
                  <p className="text-xs text-amber-700 dark:text-amber-400/80 mt-0.5">Uppdatera status och planera nya datum vid behov.</p>
                </div>
              </div>
            </div>
          )}

          {/* Address Section */}
          <div>
            <div className="flex items-center justify-between px-4 mb-1.5">
              <h3 className="text-[12px] leading-[16px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.04em]">Adress</h3>
              {formData.address.street && (
                <AiAssistButton
                  label="Fixa adress"
                  loadingLabel="Analyserar..."
                  onClick={async () => {
                    const result = await fixAddress(
                      formData.address.street,
                      formData.address.postalCode,
                      formData.address.city,
                      formData.address.accessCode,
                      formData.address.accessInfo
                    );
                    if (result && result.confidence > 0.3) {
                      setFormData(prev => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          street: result.street || prev.address.street,
                          accessCode: result.accessCode || prev.address.accessCode,
                          accessInfo: result.accessInfo || prev.address.accessInfo,
                          postalCode: result.postalCode || prev.address.postalCode,
                          city: result.city || prev.address.city,
                        }
                      }));
                    }
                  }}
                />
              )}
            </div>
            <SectionGroup>
              <FormRow label="Gatuadress" required error={fieldErrors['address.street']}>
                <input
                  type="text"
                  name="address.street"
                  value={formData.address.street}
                  onChange={handleInputChange}
                  placeholder="Ange adress"
                  autoComplete="street-address"
                  enterKeyHint="next"
                  className={inputClass}
                />
              </FormRow>
              <FormRow label="Postnummer">
                <input
                  type="text"
                  name="address.postalCode"
                  value={formData.address.postalCode}
                  onChange={handleInputChange}
                  placeholder="T.ex. 851 71"
                  autoComplete="postal-code"
                  inputMode="numeric"
                  className={inputClassRight}
                />
              </FormRow>
              <FormRow label="Ort" required error={fieldErrors['address.city']}>
                <input
                  type="text"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleInputChange}
                  placeholder="Ange ort"
                  autoComplete="address-level2"
                  enterKeyHint="next"
                  className={inputClassRight}
                />
              </FormRow>
              <FormRow label="Portkod">
                <input
                  type="text"
                  name="address.accessCode"
                  value={formData.address.accessCode}
                  onChange={handleInputChange}
                  placeholder="T.ex. 1234"
                  className={inputClassRight}
                />
              </FormRow>
              <FormRow label="Övrig info" last>
                <input
                  type="text"
                  name="address.accessInfo"
                  value={formData.address.accessInfo}
                  onChange={handleInputChange}
                  placeholder="Våning, hiss, etc."
                  className={inputClassRight}
                />
              </FormRow>
            </SectionGroup>
          </div>

          {/* Contact Section */}
          <SectionGroup title="Kontaktperson">
            <FormRow label="Namn" required error={fieldErrors['contact.name']}>
              <input
                type="text"
                name="contact.name"
                value={formData.contact.name}
                onChange={handleInputChange}
                placeholder="Kontaktperson"
                autoComplete="name"
                enterKeyHint="next"
                className={inputClass}
              />
            </FormRow>
            <FormRow label="Telefon" required error={fieldErrors['contact.phone']}>
              <input
                type="tel"
                name="contact.phone"
                value={formData.contact.phone}
                onChange={handleInputChange}
                placeholder="+46..."
                autoComplete="tel"
                className={inputClass}
              />
            </FormRow>
            <FormRow label="E-post" last>
              <input
                type="email"
                name="contact.email"
                value={formData.contact.email || ''}
                onChange={handleInputChange}
                placeholder="e-post@example.com"
                autoComplete="email"
                enterKeyHint="done"
                className={inputClass}
              />
            </FormRow>
          </SectionGroup>

          {/* Status */}
          <SectionGroup title="Status">
            <FormRow label="Status" required last>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className={selectClassRight}
              >
                {/* Labels come from StatusBadge so the form and the badge can
                    never drift apart again (they previously disagreed). */}
                {(['bostad', 'värderad', 'planerad', 'väntar', 'avbruten', 'avslutad'] as const).map(v => (
                  <option key={v} value={v}>{uiGetStatusLabel(v)}</option>
                ))}
              </select>
            </FormRow>
          </SectionGroup>

          {/* Evaluation Section */}
          <div>
            <div className="flex items-center gap-2 px-4 mb-1.5">
              <h3 className="text-[12px] leading-[16px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.04em]">Hembesök</h3>
              {formData.dates.evaluation && formData.dates.evaluation.toDate() < new Date() && (
                <span className="text-[12px] text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full font-medium">
                  Passerat
                </span>
              )}
            </div>
            <SectionGroup>
              <FormRow label="Värderare">
                <select
                  name="evaluator"
                  value={formData.evaluator}
                  onChange={handleInputChange}
                  className={selectClassRight}
                >
                  <option value="">Välj</option>
                  <option value="Anders Melin">Anders Melin</option>
                  <option value="Jan Öberg">Jan Öberg</option>
                  <option value="Peter Webb">Peter Webb</option>
                  <option value="Ilona Sweder">Ilona Sweder</option>
                </select>
              </FormRow>
              <FormRow label="Datum">
                <input
                  type="date"
                  name="dates.evaluation"
                  value={formData.dates.evaluation ? new Intl.DateTimeFormat('sv-SE').format(formData.dates.evaluation.toDate()) : ''}
                  onChange={handleDateChange}
                  className={inputClassRight}
                />
              </FormRow>
              <FormRow label="Starttid">
                <TimeSelect
                  id="dates.evaluationStartTime"
                  name="dates.evaluationStartTime"
                  value={formData.dates.evaluationStartTime}
                  onChange={handleTimeChange}
                  date={formData.dates.evaluation?.toDate()}
                  availableSlots={evaluationDateSlots}
                  isLoading={loadingEvaluationSlots}
                  className={selectClassRight}
                />
              </FormRow>
              <FormRow label="Sluttid" last>
                <TimeSelect
                  id="dates.evaluationEndTime"
                  name="dates.evaluationEndTime"
                  value={formData.dates.evaluationEndTime}
                  onChange={handleInputChange}
                  className={selectClassRight}
                />
              </FormRow>
            </SectionGroup>
          </div>

          {/* Pickup Section */}
          <div ref={pickupSectionRef as React.RefObject<HTMLDivElement>} data-section="pickup">
            <div className="flex items-center gap-2 px-4 mb-1.5">
              <h3 className="text-[12px] leading-[16px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.04em]">Upphämtning</h3>
              {formData.dates.scheduledPickup && formData.dates.scheduledPickup.toDate() < new Date() && (
                <span className="text-[12px] text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full font-medium">
                  Passerat
                </span>
              )}
            </div>
            <SectionGroup>
              <FormRow label="Datum">
                <input
                  type="date"
                  name="dates.scheduledPickup"
                  value={formData.dates.scheduledPickup ? new Intl.DateTimeFormat('sv-SE').format(formData.dates.scheduledPickup.toDate()) : ''}
                  onChange={handleDateChange}
                  className={inputClassRight}
                />
              </FormRow>
              <FormRow label="Tid">
                <TimeSelect
                  id="dates.scheduledPickupTime"
                  name="dates.scheduledPickupTime"
                  value={formData.dates.scheduledPickupTime}
                  onChange={handleInputChange}
                  date={formData.dates.scheduledPickup?.toDate()}
                  availableSlots={pickupDateSlots}
                  isLoading={loadingPickupSlots}
                  className={selectClassRight}
                />
              </FormRow>
              {/* Team members */}
              <div className="px-4 py-3 min-h-[44px]">
                <p className="text-[15px] text-[var(--text-primary)] mb-2">Team</p>
                {formData.pickupTeam.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {formData.pickupTeam.map((member, index) => (
                      <span key={index} className="inline-flex items-center gap-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-full px-2.5 py-1 text-[13px]">
                        {member}
                        <button
                          type="button"
                          onClick={() => {
                            const newTeam = [...formData.pickupTeam];
                            newTeam.splice(index, 1);
                            setFormData(prev => ({ ...prev, pickupTeam: newTeam }));
                          }}
                          aria-label={`Ta bort ${member} från teamet`}
                          className="text-emerald-400 dark:text-emerald-500 hover:text-red-500 -mr-0.5"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      setFormData(prev => ({
                        ...prev,
                        pickupTeam: [...prev.pickupTeam, e.target.value.trim()]
                      }));
                    }
                  }}
                  className={`${selectClass} text-[var(--text-tertiary)] mb-2`}
                >
                  <option value="">Välj teammedlem...</option>
                  {TEAM_MEMBERS.filter(m => !formData.pickupTeam.includes(m)).map((member, index) => (
                    <option key={index} value={member}>{member}</option>
                  ))}
                </select>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={customTeamMember}
                    onChange={(e) => setCustomTeamMember(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && customTeamMember.trim()) {
                        e.preventDefault();
                        setFormData(prev => ({
                          ...prev,
                          pickupTeam: [...prev.pickupTeam, customTeamMember.trim()]
                        }));
                        setCustomTeamMember('');
                      }
                    }}
                    placeholder="Annan person..."
                    className={inputClass}
                  />
                  {customTeamMember.trim() && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          pickupTeam: [...prev.pickupTeam, customTeamMember.trim()]
                        }));
                        setCustomTeamMember('');
                      }}
                      className="text-accent-600 active:text-accent-700 font-medium text-[15px] flex-shrink-0"
                    >
                      Lägg till
                    </button>
                  )}
                </div>
              </div>
            </SectionGroup>
          </div>

          {/* Calendar overlap warning */}
          {parsedOverlapWarning && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500 dark:border-blue-400 rounded-ios-card px-3 py-2.5 text-xs">
              <div className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5 flex-shrink-0">
                  <Calendar size={14} />
                </span>
                <div>
                  <p className="font-medium text-blue-800 dark:text-blue-300 text-[13px]">
                    {parsedOverlapWarning.message}
                  </p>
                  <ul className="mt-1.5 text-blue-700 dark:text-blue-400/80">
                    {(parsedOverlapWarning.events || []).map((event: any, index: number) => (
                      <li key={index} className="flex items-start gap-1 mb-0.5">
                        <span>•</span>
                        <span>"{event.summary}" {event.location && `(${event.location})`} — {event.time}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1.5 text-blue-600 dark:text-blue-400/70">
                    {parsedOverlapWarning.type === 'internal'
                      ? 'Tiderna överlappar. Justera vid behov.'
                      : 'Kontrollera transporttid mellan platser.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notes Section */}
          <div>
            <div className="flex items-center justify-between px-4 mb-1.5">
              <h3 className="text-[12px] leading-[16px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.04em]">Anteckningar</h3>
              {formData.generalNotes.trim().length > 5 && (
                <AiAssistButton
                  label="Språkgranska"
                  loadingLabel="Granskar..."
                  onClick={async () => {
                    const result = await polishText(formData.generalNotes, 'projektanteckningar');
                    if (result && result.text) {
                      setFormData(prev => ({ ...prev, generalNotes: result.text }));
                    }
                  }}
                />
              )}
            </div>
            <SectionGroup>
              <div className="px-4 py-3">
                <textarea
                  name="generalNotes"
                  value={formData.generalNotes}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Anteckningar om projektet..."
                  className="w-full bg-transparent text-[15px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none resize-none"
                />
              </div>
            </SectionGroup>
            <p className="text-xs text-[var(--text-tertiary)] px-4 mt-1.5">Synkas automatiskt till Google Kalender.</p>
          </div>

          {/* Temporary Photos */}
          {tempPhotoCount > 0 && (
            <SectionGroup title="Temporära foton">
              <button
                type="button"
                onClick={() => setShowTempPhotos(!showTempPhotos)}
                className="flex items-center gap-3 px-4 py-3.5 min-h-[44px] w-full text-left press-row"
              >
                <Camera size={20} className="text-amber-500 flex-shrink-0" />
                <span className="flex-1 text-[15px] text-[var(--text-primary)]">
                  {showTempPhotos ? 'Dölj foton' : 'Visa foton'}
                </span>
                <span className="text-[13px] text-[var(--text-secondary)] bg-[var(--bg-surface-alt)] px-2 py-0.5 rounded-full font-medium">
                  {tempPhotoCount}
                </span>
              </button>
              {showTempPhotos && (
                <div className="px-4 pb-4 border-t border-[var(--border-subtle)]">
                  <div className="pt-3">
                    <TemporaryPhotoComponent />
                    <p className="text-xs text-[var(--text-tertiary)] mt-2">
                      Kopplas till projektet när du sparar.
                    </p>
                  </div>
                </div>
              )}
            </SectionGroup>
          )}

          {/* Calendar Sync Info */}
          {isEditMode && project && (
            <SectionGroup title="Kalender">
              <div className="px-4 py-3">
                <CalendarSyncStatus project={project} />
              </div>
            </SectionGroup>
          )}

        </form>
      </PageShell>
    </>
  );
};

export default ProjectFormPage;